import { useEffect, useState } from "react";
import { toast } from "sonner";
import { NotebookPen, BellRing } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { notifState } from "@/lib/reminders";
import { pushSupported, detectTimezone, currentSubscription, subscribePush } from "@/lib/push";

// Paramètres → « Rappel du journal » : optionnel, désactivé par défaut, tous niveaux, jamais culpabilisant.
export function JournalReminderRow({ settings, save }) {
  const { t } = useLang();
  const rem = settings.journal_reminder || { enabled: false, time: "20:00" };
  const [perm, setPerm] = useState(notifState());
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setPushOn(!!s)); }, []);
  const update = (patch) => save({ journal_reminder: { enabled: rem.enabled, time: rem.time || "20:00", ...patch } });

  const allow = async () => {
    setBusy(true);
    try {
      if (pushSupported()) { await subscribePush(detectTimezone()); setPushOn(true); }
      else if (typeof Notification !== "undefined") await Notification.requestPermission();
      setPerm(notifState());
      if (notifState() === "granted") toast.success(t("notifications_allowed"));
    } catch (e) { setPerm(notifState()); if (e.message === "denied") toast.error(t("notifications_blocked")); else console.error("journal push", e); }
    finally { setBusy(false); }
  };
  const sendTest = async () => { try { const { data } = await api.post("/push/test-journal"); toast.success(t("push_test_sent", { n: data.sent })); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  return (
    <div className="chenia-card p-5" data-testid="settings-journal-reminder">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-2xl bg-secondary text-primary"><NotebookPen className="h-5 w-5" /></span>
          <div>
            <p className="font-medium">{t("j_rem_title")}</p>
            <p className="text-xs text-muted-foreground">{t("j_rem_sub")}</p>
          </div>
        </div>
        <Switch checked={!!rem.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="journal-reminder-enabled" />
      </div>
      {rem.enabled && (
        <div className="mt-4 space-y-3" data-testid="journal-reminder-options">
          <label className="text-sm inline-flex items-center gap-2">{t("reminder_time")}
            <Input type="time" value={rem.time || "20:00"} onChange={(e) => update({ time: e.target.value })} className="rounded-xl w-32" data-testid="journal-reminder-time" />
          </label>
          <p className="text-xs text-muted-foreground" data-testid="journal-reminder-example">« {t("j_rem_body")} »</p>
          <div className="flex flex-wrap items-center gap-2">
            {perm === "granted" ? (
              <span className="text-xs rounded-full px-2.5 py-1 bg-chenia-sage/40" data-testid="journal-reminder-status">{pushOn ? t("push_active") : t("notifications_allowed")}</span>
            ) : (
              <Button size="sm" onClick={allow} disabled={busy || perm === "denied"} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="journal-reminder-allow"><BellRing className="h-4 w-4 mr-1" /> {perm === "denied" ? t("notifications_blocked") : t("allow_notifications")}</Button>
            )}
            {pushOn && <Button size="sm" variant="outline" onClick={sendTest} className="rounded-full" data-testid="journal-reminder-test">{t("push_send_test")}</Button>}
          </div>
          <p className="text-[11px] text-muted-foreground">{pushOn ? t("j_rem_notice_push") : t("reminder_notice")}</p>
        </div>
      )}
    </div>
  );
}
